import { defineStore } from 'pinia';

/**
 * Hand-off for the admin Promote flow. The Draw tool stashes the drawn/uploaded
 * geometry here (as an EPSG:4326 GeoJSON object) + the parent project id, then
 * routes to `/promote` — navigating away unmounts the zone view, so route state
 * is too fragile to carry the payload. Cleared once the staging zone is created.
 */
export interface PromoteDraft {
  /** EPSG:4326 GeoJSON (FeatureCollection) as produced by OpenLayers' GeoJSON writer. */
  geojson: object;
  /**
   * Parent project (`zone:02`) id from the active zone context. `null` in
   * exploration mode — the `zone:04` shell doesn't exist yet; it's minted on
   * submit (`POST /zones/explore`) and the drawing stored into it.
   */
  projectId: number | null;
  /** True when saving a draw-only Explore session as a new `zone:04` exploration. */
  isExploration?: boolean;
  featureCount: number;
  areaHa: number;
}

/** A staging zone removed this session, kept so a stale read can't resurrect it. */
export interface RemovedStagingZone {
  zone: number;
  /** Parent project id, so we only filter/forget within the right project. */
  projectId: number;
}

export const usePromoteStore = defineStore('promote', {
  state: () => ({
    draft: null as PromoteDraft | null,
    /**
     * Tombstones for staging zones the user removed this session. After a
     * `DELETE /zones/store/{id}` the BE de-appends it, but the project read the
     * FE refreshes through is briefly stale — CloudFront edge cache on
     * `GET /zones?zone_id=…` plus DynamoDB eventual consistency on the zones
     * GSI — so a store→navigate that re-fetches the project can resurrect a
     * just-removed zone. We hide tombstoned ids until the authoritative read
     * drops them (see `details.view`), then forget the tombstone. In-memory
     * only (a full reload clears it; by then the read has usually caught up).
     */
    removedStagingZones: [] as RemovedStagingZone[],
  }),
  actions: {
    setDraft(draft: PromoteDraft): void {
      this.draft = draft;
    },
    clear(): void {
      this.draft = null;
    },
    markStagingZoneRemoved(zone: number, projectId: number): void {
      if (!this.removedStagingZones.some((t) => t.zone === zone)) {
        this.removedStagingZones.push({ zone, projectId });
      }
    },
    forgetRemovedStagingZone(zone: number): void {
      this.removedStagingZones = this.removedStagingZones.filter((t) => t.zone !== zone);
    },
    removedStagingZoneIdsForProject(projectId: number): number[] {
      return this.removedStagingZones.filter((t) => t.projectId === projectId).map((t) => t.zone);
    },
  },
});
